import { useMemo, useState } from "react";

type SliceType = "ABSOLUTE" | "PERCENTAGE" | "REMAINDER";

// Structural shapes so both the categorize board (PoolEntry/Category) and the
// overview grid (reconstructed from cost-tree slice leaves) can drive the dialog.
type EditorEntry = {
  stakeholder: { name: string };
  allocations: ReadonlyArray<{
    category: { id: string };
    sliceType?: string | null;
    amountCents?: number | null;
    weight: number;
  }>;
};
type EditorCategory = { id: string; name: string; parent?: { readonly id: string } | null };

type Slice = {
  key: string;
  categoryId: string;
  sliceType: SliceType;
  amount: number | null; // dollars, ABSOLUTE only
  percent: number | null; // whole percent, PERCENTAGE only
  adjusted: boolean; // the auto-cap touched this slice
};

export type SliceAllocation = {
  categoryId: string;
  sliceType: SliceType;
  ordinal: number;
  amountCents?: number;
  weight?: number;
};

const TYPE_RANK: Record<SliceType, number> = { ABSOLUTE: 0, PERCENTAGE: 1, REMAINDER: 2 };

let keySeq = 0;
const nextKey = () => `slice-${keySeq++}`;

// Edits a stakeholder's complete allocation as an ordered typed-slice waterfall
// (absolute amounts off the top → percentages of what's left → a remainder).
// Saving sends the whole ordered list through the categorize mutation; an empty
// list clears the stakeholder back to uncategorized. Replaces the old
// percentage-only editor everywhere it was used.
export default function SplitEditor({
  entry,
  categories,
  onClose,
  onSave,
}: {
  entry: EditorEntry;
  categories: readonly EditorCategory[];
  onClose: () => void;
  onSave: (allocations: SliceAllocation[]) => void;
}) {
  const [slices, setSlices] = useState<Slice[]>(() =>
    entry.allocations.map((allocation) => {
      const sliceType = (allocation.sliceType ?? "PERCENTAGE") as SliceType;
      return {
        key: nextKey(),
        categoryId: allocation.category.id,
        sliceType,
        amount: sliceType === "ABSOLUTE" ? (allocation.amountCents ?? 0) / 100 : null,
        percent: sliceType === "PERCENTAGE" ? Math.round(allocation.weight * 100) : null,
        adjusted: false,
      };
    })
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const options = useMemo(() => {
    const byId = new Map(categories.map((category) => [category.id, category]));
    return [...categories]
      .map((category) => ({
        id: category.id,
        label: category.parent
          ? `${byId.get(category.parent.id)?.name ?? ""} › ${category.name}`
          : category.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [categories]);

  // Keep the list in waterfall order (absolute → percentage → remainder),
  // preserving within-group order so up/down reordering is stable.
  const ordered = useMemo(
    () => [...slices].sort((a, b) => TYPE_RANK[a.sliceType] - TYPE_RANK[b.sliceType]),
    [slices]
  );
  const hasRemainder = slices.some((slice) => slice.sliceType === "REMAINDER");
  const percentTotal = slices
    .filter((slice) => slice.sliceType === "PERCENTAGE")
    .reduce((sum, slice) => sum + (slice.percent ?? 0), 0);

  const valid = slices.every(
    (slice) =>
      slice.categoryId &&
      (slice.sliceType !== "ABSOLUTE" || (slice.amount ?? 0) > 0)
  );

  const patch = (key: string, change: Partial<Slice>) =>
    setSlices((previous) => previous.map((slice) => (slice.key === key ? { ...slice, ...change, adjusted: false } : slice)));

  // R17/AE10: when entered percentages would exceed 100% of the post-absolute
  // base, cap the slice the user just changed and flag it as adjusted.
  const setPercent = (key: string, value: number) => {
    setSlices((previous) => {
      const others = previous
        .filter((slice) => slice.key !== key && slice.sliceType === "PERCENTAGE")
        .reduce((sum, slice) => sum + (slice.percent ?? 0), 0);
      const capped = others + value > 100;
      const percent = capped ? Math.max(0, 100 - others) : value;
      return previous.map((slice) => (slice.key === key ? { ...slice, percent, adjusted: capped } : slice));
    });
  };

  const addSlice = (sliceType: SliceType) => {
    const used = new Set(slices.map((slice) => slice.categoryId));
    const firstFree = options.find((option) => !used.has(option.id));
    setSlices((previous) => [
      ...previous,
      {
        key: nextKey(),
        categoryId: firstFree?.id ?? options[0]?.id ?? "",
        sliceType,
        amount: sliceType === "ABSOLUTE" ? 0 : null,
        percent: sliceType === "PERCENTAGE" ? Math.max(0, 100 - percentTotal) : null,
        adjusted: false,
      },
    ]);
  };

  const remove = (key: string) => setSlices((previous) => previous.filter((slice) => slice.key !== key));

  // Up/down within a type group; cross-type moves are blocked (the groups have a
  // fixed waterfall order).
  const move = (key: string, direction: -1 | 1) => {
    setSlices((previous) => {
      const slice = previous.find((s) => s.key === key);
      if (!slice) return previous;
      const group = previous.filter((s) => s.sliceType === slice.sliceType);
      const index = group.findIndex((s) => s.key === key);
      const target = index + direction;
      if (target < 0 || target >= group.length) return previous;
      [group[index], group[target]] = [group[target], group[index]];
      const others = previous.filter((s) => s.sliceType !== slice.sliceType);
      return [...others, ...group];
    });
  };

  const save = () => {
    const allocations: SliceAllocation[] = ordered.map((slice, index) => ({
      categoryId: slice.categoryId,
      sliceType: slice.sliceType,
      ordinal: index,
      ...(slice.sliceType === "ABSOLUTE" ? { amountCents: Math.round((slice.amount ?? 0) * 100) } : {}),
      ...(slice.sliceType === "PERCENTAGE" ? { weight: (slice.percent ?? 0) / 100 } : {}),
    }));
    try {
      onSave(allocations);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not save allocation");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-[540px] max-h-[85vh] overflow-y-auto p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800 mb-1">Split {entry.stakeholder.name}</h2>
        <p className="text-xs text-gray-500 mb-4">
          A monthly waterfall: fixed amounts come off the top, percentages take a
          share of what's left, the remainder catches the rest. Anything left
          unallocated reads as uncategorized.
        </p>

        <div className="flex flex-col gap-2">
          {ordered.map((slice) => (
            <SliceRow
              key={slice.key}
              slice={slice}
              options={options}
              onCategory={(categoryId) => patch(slice.key, { categoryId })}
              onAmount={(amount) => patch(slice.key, { amount })}
              onPercent={(percent) => setPercent(slice.key, percent)}
              onUp={() => move(slice.key, -1)}
              onDown={() => move(slice.key, 1)}
              onRemove={() => remove(slice.key)}
            />
          ))}
          {slices.length === 0 && (
            <p className="text-xs text-gray-400 py-2">No slices — add one below, or save to leave uncategorized.</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
          <button type="button" onClick={() => addSlice("ABSOLUTE")} className="text-emerald-600 hover:text-emerald-800">
            + Absolute
          </button>
          <button type="button" onClick={() => addSlice("PERCENTAGE")} className="text-emerald-600 hover:text-emerald-800">
            + Percentage
          </button>
          {!hasRemainder && (
            <button type="button" onClick={() => addSlice("REMAINDER")} className="text-emerald-600 hover:text-emerald-800">
              + Remainder
            </button>
          )}
          <span className={`ml-auto tabular-nums ${percentTotal > 100 ? "text-red-600 font-semibold" : "text-gray-500"}`}>
            {percentTotal}% of base
          </span>
        </div>

        {saveError && <p className="mt-3 text-xs text-red-600">{saveError}</p>}

        <div className="flex items-center gap-2 mt-5">
          <button
            type="button"
            disabled={!valid && slices.length > 0}
            onClick={save}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            Save allocation
          </button>
          {entry.allocations.length > 0 && (
            <button
              type="button"
              onClick={() => onSave([])}
              className="px-3 py-1.5 text-sm rounded border border-red-200 text-red-600 hover:bg-red-50"
            >
              Clear (uncategorize)
            </button>
          )}
          <button type="button" onClick={onClose} className="ml-auto text-sm text-gray-500 hover:text-gray-800">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SliceRow({
  slice,
  options,
  onCategory,
  onAmount,
  onPercent,
  onUp,
  onDown,
  onRemove,
}: {
  slice: Slice;
  options: { id: string; label: string }[];
  onCategory: (id: string) => void;
  onAmount: (value: number) => void;
  onPercent: (value: number) => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) {
  const badge =
    slice.sliceType === "ABSOLUTE" ? "fixed $" : slice.sliceType === "PERCENTAGE" ? "% of base" : "remainder";
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-[11px] uppercase tracking-wide text-gray-400">{badge}</span>
      <select
        value={slice.categoryId}
        onChange={(event) => onCategory(event.target.value)}
        className="flex-1 min-w-0 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
      >
        <option value="" disabled>
          Pick a category…
        </option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>

      {slice.sliceType === "ABSOLUTE" && (
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-400">$</span>
          <input
            type="number"
            min={0}
            value={slice.amount ?? ""}
            onChange={(event) => onAmount(Number(event.target.value))}
            className="w-24 text-sm border border-gray-300 rounded px-2 py-1.5 text-right tabular-nums"
          />
        </div>
      )}
      {slice.sliceType === "PERCENTAGE" && (
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={0}
            max={100}
            value={slice.percent ?? ""}
            onChange={(event) => onPercent(Number(event.target.value))}
            className="w-16 text-sm border border-gray-300 rounded px-2 py-1.5 text-right tabular-nums"
          />
          <span className="text-sm text-gray-400">%</span>
          {slice.adjusted && <span className="text-[10px] text-amber-600">adjusted</span>}
        </div>
      )}

      {slice.sliceType !== "REMAINDER" && (
        <div className="flex flex-col leading-none">
          <button type="button" onClick={onUp} title="Move up" className="text-gray-300 hover:text-gray-600 text-xs">
            ▲
          </button>
          <button type="button" onClick={onDown} title="Move down" className="text-gray-300 hover:text-gray-600 text-xs">
            ▼
          </button>
        </div>
      )}
      <button type="button" onClick={onRemove} title="Remove" className="text-gray-300 hover:text-red-500">
        ✕
      </button>
    </div>
  );
}
